import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Collapse,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import IconOnlyChip from "../components/IconOnlyChip";
import IconTextChip from "../components/IconTextChip";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import DataGridTable from "../components/DataGridTable";
import AdminControlTabs from "../components/AdminControlTabs";
import { useAuth } from "../components/AuthContext";
import { useInstitution } from "../components/InstitutionContext";
import NoPermissionPage from "./NoPermissionPage";
import { userHasInstitutionRole } from "../utils/permissions";
import api, {
  updateDepartment,
  updateCourse,
  updateProfessorCourse,
} from "../services/api";
import { formatPrevSubmission } from "../utils/formatPrevSubmission";
import { ordinalSuffix } from "../utils/ordinalSuffix";
import ActionReasonDialog from "../components/ActionReasonDialog";
import { isReviewEdited } from "../utils/isEdited";
import EditedTag from "../components/EditedTag";
import EditDepartmentAdminDialog from "../components/EditDepartmentAdminDialog";
import EditCourseAdminDialog from "../components/EditCourseAdminDialog";
import EditProfessorCourseAdminDialog from "../components/EditProfessorCourseAdminDialog";

export const getApproveEndpoint = (type, id, row) => {
  if (type === "professor-course") {
    const [profId, courseId] = id.split("-");
    return `/courses/admin-dashboard/approve-professor-course/${profId}/${courseId}/`;
  }
  if (type === "professor-submission") {
    return `/courses/admin-dashboard/approve-professor-submission/${id}/`;
  }
  if (type === "professor" && row?.unapproved_link_id) {
    return `/courses/admin-dashboard/approve-professor-course-link/${row.unapproved_link_id}/`;
  }
  return `/courses/admin-dashboard/approve-${type}/${id}/`;
};

export const getRejectEndpoint = (type, id, row) => {
  if (type === "professor-course") {
    const [profId, courseId] = id.split("-");
    return `/courses/admin-dashboard/reject-professor-course/${profId}/${courseId}/`;
  }
  if (type === "professor-submission") {
    return `/courses/admin-dashboard/reject-professor-submission/${id}/`;
  }
  if (type === "professor" && row?.unapproved_link_id) {
    return `/courses/admin-dashboard/reject-professor-course-link/${row.unapproved_link_id}/`;
  }
  return `/courses/admin-dashboard/reject-${type}/${id}/`;
};

export const DashboardTable = ({
  title,
  data = [],
  type,
  handleApprove,
  handleReject,
  handleUnapprove,
  showReason = false,
  showResubCount = false,
  showDiff = false,
  showStatus = false,
  showCourse = true,
}) => {
  const extraCols =
    (showReason ? 1 : 0) +
    (showResubCount ? 1 : 0) +
    (showDiff ? 1 : 0) +
    (showStatus ? 1 : 0);
  const hasEntry =
    type === "mixed" || (data[0] && Object.prototype.hasOwnProperty.call(data[0], "entry"));
  const hasProfessor =
    data.length > 0
      ? data.some(
          (d) =>
            (d.type || type) === "professor" ||
            (d.type || type) === "professor-course" ||
            (d.type || type) === "professor-submission"
        )
      :
        type === "professor" ||
        type === "professor-course" ||
        type === "professor-submission";
  const hasCourseColumn =
    showCourse &&
    (hasEntry
      ? hasProfessor
      : type === "professor" || type === "professor-course" || type === "professor-submission");
  const columns = [];

  if (hasEntry) {
    columns.push({ field: "entry", headerName: "Entry", flex: 1, minWidth: 150 });
  } else if (type === "course") {
    columns.push({ field: "short_name", headerName: "Short Name", minWidth: 120 });
    columns.push({ field: "long_name", headerName: "Long Name", flex: 1, minWidth: 150 });
  } else {
    columns.push({ field: "name", headerName: "Name", flex: 1, minWidth: 150 });
  }

  columns.push({
    field: "department",
    headerName: "Department",
    minWidth: 120,
    valueGetter: (params) => {
      const row = params?.row || {};
      const rowType = hasEntry ? row.type : type;
      return rowType === "professor" || rowType === "professor-course"
        ? row.department?.name || "—"
        : row.department_details?.name || row.department?.name || "—";
    },
  });

  if (hasCourseColumn) {
    columns.push({
      field: "course",
      headerName: "Course",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        const row = params?.row || {};
        return row.courses_info?.[0]
          ? `${row.courses_info[0].short_name} – ${row.courses_info[0].long_name}`
          : "—";
      },
    });
  }

  if (showResubCount) {
    columns.push({
      field: "resubmissions",
      headerName: "Resubmissions",
      width: 120,
      valueGetter: (params) => {
        const row = params?.row || {};
        return Math.max((row.submission_count || 1) - 1, 0);
      },
    });
  }

  if (showDiff) {
    columns.push({
      field: "previous",
      headerName: "Previous Submission",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        const row = params?.row || {};
        return formatPrevSubmission(row.previous_submission);
      },
    });
  }

  if (showReason) {
    columns.push({ field: "rejection_reason", headerName: "Rejection Reason", flex: 1, minWidth: 150 });
  }

  if (showStatus) {
    columns.push({
      field: "status",
      headerName: "Status",
      minWidth: 120,
      valueGetter: (params) => {
        const row = params?.row || {};
        return row.is_rejected
          ? "Rejected"
          : showResubCount
          ? "Resubmitted"
          : `Resubmitted (${ordinalSuffix(row.submission_count || 1)} try)`;
      },
    });
  }

  columns.push({
    field: "action",
    headerName: "Action",
    sortable: false,
    width: 220,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => {
      const rowType = hasEntry ? row.type : type;
      const disabled =
        row.is_rejected ||
        (rowType === "professor-course" && !row.course_is_approved) ||
        (rowType === "professor" && row.has_unapproved_link);

      let tooltip = "";
      if (rowType === "professor" && row.has_unapproved_link) {
        tooltip = `${row.unapproved_course_name || row.courses_info?.[0]?.short_name} course must be approved first`;
      } else if (rowType === "professor-course" && !row.course_is_approved) {
        tooltip = `${row.courses_info?.[0]?.short_name || row.course_short_name || ""} course must be approved first`;
      } else if (row.is_rejected) {
        tooltip = "Submission has been rejected";
      }

      return !row.is_approved ? (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <Tooltip title={tooltip} placement="top" disableHoverListener={!tooltip} describeChild>
            <span>
              <IconTextChip
                icon={<CheckIcon fontSize="small" />}
                label="Approve"
                color="success"
                disabled={disabled}
                aria-label={tooltip || undefined}
                onClick={() => handleApprove(rowType, row.id, row)}
              />
            </span>
          </Tooltip>
          <Tooltip title={tooltip} placement="top" disableHoverListener={!tooltip} describeChild>
            <span>
              <IconTextChip
                icon={<CloseIcon fontSize="small" />}
                label="Reject"
                color="error"
                disabled={disabled}
                aria-label={tooltip || undefined}
                onClick={() => handleReject(rowType, row.id, row)}
              />
            </span>
          </Tooltip>
        </Box>
      ) : (
        <IconTextChip
          icon={<UndoIcon fontSize="small" />}
          label="Unapprove"
          color="warning"
          onClick={() => handleUnapprove(rowType, row.id)}
        />
      );
    },
  });

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 400,
        maxHeight: 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <DataGridTable rows={data} columns={columns} pageSize={5} sx={{ flex: 1 }} />
    </Box>
  );
};

export const ReportedReviewsTable = ({
  data = [],
  handleModerate,
  heldReviews = {},
  handleHoldToggle,
  reviewDecisions = {},
}) => {
  const [expandedHistory, setExpandedHistory] = useState({});

  const toggleHistory = (id) => {
    setExpandedHistory((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const columns = [
    { field: "course", headerName: "Course", minWidth: 130 },
    { field: "professor", headerName: "Professor", minWidth: 130 },
    {
      field: "report_count",
      headerName: "Reports",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "reasons",
      headerName: "Reasons",
      width: 200,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "content",
      headerName: "Content",
      flex: 1,
      minWidth: 350,
      sortable: false,
      renderCell: ({ row }) => (
          <Box sx={{ width: "100%" }}>
            <Box>
              {row.content}
              {isReviewEdited(row) && <EditedTag />}
            </Box>
            {row.edit_history.length > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, cursor: "pointer", color: "primary.main" }}
                onClick={() => toggleHistory(row.id)}
              >
                {expandedHistory[row.id]
                  ? "- Hide Edit History"
                  : "+ Show Edit History"}
              </Typography>
              <Collapse in={expandedHistory[row.id]} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 2, pt: 1 }}>
                  {row.edit_history.map((h, idx) => (
                    <Typography variant="body2" key={idx} sx={{ mb: 0.5 }}>
                      {`${new Date(h.edited_at).toLocaleString()}: ${h.old_content}`}
                    </Typography>
                  ))}
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 240,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const voted = !!reviewDecisions[row.id];
        const tooltipProps = {
          title: "Voted. Awaiting final decision.",
          placement: "top",
          describeChild: true,
        };

        const withTooltip = (btn) =>
          voted ? <Tooltip {...tooltipProps}><span>{btn}</span></Tooltip> : btn;

        return (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            {withTooltip(
              <Button
                variant={heldReviews[row.id] ? "contained" : "outlined"}
                size="small"
                disabled={voted}
                aria-label={voted ? "Voted. Awaiting final decision." : undefined}
                onClick={() => handleHoldToggle(row.id)}
              >
                Hold
              </Button>
            )}
            {withTooltip(
              <Button
                variant={reviewDecisions[row.id] === "REMOVE" ? "contained" : "outlined"}
                size="small"
                color="error"
                disabled={heldReviews[row.id] || voted}
                aria-label={voted ? "Voted. Awaiting final decision." : undefined}
                onClick={() => handleModerate(row.id, "REMOVE")}
              >
                Remove
              </Button>
            )}
            {withTooltip(
              <Button
                variant={reviewDecisions[row.id] === "DISMISS" ? "contained" : "outlined"}
                size="small"
                color="info"
                disabled={heldReviews[row.id] || voted}
                aria-label={voted ? "Voted. Awaiting final decision." : undefined}
                onClick={() => handleModerate(row.id, "DISMISS")}
              >
                Dismiss
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  const rows = data.map((r) => ({
    id: r.id,
    course: r.course,
    professor: r.professor || "—",
    report_count: r.report_count,
    reasons: Object.entries(r.reason_counts || {})
      .map(([reason, count]) => `${reason}: ${count}`)
      .join(", "),
    content: r.content,
    edit_history: r.edit_history || [],
  }));

  return (
    <Box sx={{ overflowX: "auto", mb: 4 }}>
      <DataGridTable
        rows={rows}
        columns={columns}
        pageSize={5}
        sx={{ minWidth: 1200 }}
      />
    </Box>
  );
};

export const mergePendingLists = (
  pendingDepartments = [],
  pendingCourses = [],
  pendingProfessors = []
) => {
  const deptMap = new Map();
  const courseMap = new Map();
  const profMap = new Map();

const addDept = (d, pending = false, upstream = false) => {
  if (!d) return;
  const existing = deptMap.get(d.id);
  deptMap.set(d.id, {
    id: d.id,
    name: d.name,
    rejection_reason:
      d.rejection_reason !== undefined
        ? d.rejection_reason
        : existing?.rejection_reason,
    previous_submission:
      d.previous_submission !== undefined
        ? d.previous_submission
        : existing?.previous_submission,
    is_pending: pending || existing?.is_pending,
    is_upstream: upstream || existing?.is_upstream,
    is_rejected:
      d.is_rejected !== undefined
        ? d.is_rejected
        : existing?.is_rejected,
    submission_count:
      d.submission_count !== undefined
        ? d.submission_count
        : existing?.submission_count,
  });
};

const addCourse = (c, pending = false, upstream = false) => {
  if (!c) return;
  const dep = c.department_details || c.department;
  const existing = courseMap.get(c.id);
  courseMap.set(c.id, {
    id: c.id,
    short_name: c.short_name,
    long_name: c.long_name,
    department: dep,
    rejection_reason:
      c.rejection_reason !== undefined
        ? c.rejection_reason
        : existing?.rejection_reason,
    previous_submission:
      c.previous_submission !== undefined
        ? c.previous_submission
        : existing?.previous_submission,
    is_pending: pending || existing?.is_pending,
    is_upstream: upstream || existing?.is_upstream,
    is_rejected:
      c.is_rejected !== undefined
        ? c.is_rejected
        : existing?.is_rejected,
    submission_count:
      c.submission_count !== undefined
        ? c.submission_count
        : existing?.submission_count,
  });
  if (dep) addDept(dep, false, pending || upstream || c.is_rejected);
};

const addProfessor = (p, pending = false, upstream = false) => {
  if (!p) return;
  const existing = profMap.get(p.id) || { courses_info: [] };
  const courses = [...(existing.courses_info || [])];
  if (p.courses_info) {
    p.courses_info.forEach((ci) => {
      if (!courses.some((c) => c.id === ci.id)) courses.push(ci);
    });
  }
  profMap.set(p.id, {
    id: p.id,
    name: p.name,
    rejection_reason:
      p.rejection_reason !== undefined
        ? p.rejection_reason
        : existing.rejection_reason,
    previous_submission:
      p.previous_submission !== undefined
        ? p.previous_submission
        : existing.previous_submission,
    is_pending: pending || existing.is_pending,
    is_upstream: upstream || existing.is_upstream,
    is_rejected:
      p.is_rejected !== undefined ? p.is_rejected : existing.is_rejected,
    submission_count:
      p.submission_count !== undefined
        ? p.submission_count
        : existing.submission_count,
    courses_info: courses,
    has_unapproved_link: p.has_unapproved_link || existing.has_unapproved_link || false,
    unapproved_course_name: p.unapproved_course_name || existing.unapproved_course_name,
    unapproved_course_id: p.unapproved_course_id || existing.unapproved_course_id,
    unapproved_link_id: p.unapproved_link_id || existing.unapproved_link_id,
    is_submission: p.is_submission || existing.is_submission || false,
  });
};

  pendingDepartments.forEach((d) => addDept(d, !d.is_rejected));
  pendingCourses.forEach((c) => addCourse(c, !c.is_rejected));
  pendingProfessors.forEach((p) => {
    addProfessor(p, !p.is_rejected);
    (p.courses_info || []).forEach((ci) => addCourse(ci, false, true));
  });

  courseMap.forEach((c) => {
    if (c.department) addDept(c.department, false, c.is_pending || c.is_upstream);
  });

  return {
    departments: Array.from(deptMap.values()).filter(
      (d) => d.is_pending || d.is_upstream || d.is_rejected
    ),
    courses: Array.from(courseMap.values()).filter(
      (c) => c.is_pending || c.is_upstream || c.is_rejected
    ),
    professors: Array.from(profMap.values()).filter(
      (p) => p.is_pending || p.is_upstream || p.is_rejected
    ),
  };
};

export const buildUniquePendingList = (
  pendingDepartments = [],
  pendingCourses = [],
  pendingProfCourseLinks = []
) => {
  const pendingList = [
    ...pendingDepartments.map((d) => ({ ...d, type: 'department' })),
    ...pendingCourses.map((c) => ({
      ...c,
      type: 'course',
      department: c.department_details || c.department,
    })),
    ...pendingProfCourseLinks.map((l) => {
      const prof = l.professor_submission || l.professor || {};
      const profId = prof.id;
      return {
        id: `${profId}-${l.course.id}`,
        professor_id: profId,
        course_id: l.course.id,
        courses_info: [l.course],
        department: l.course.department,
        course_is_approved: l.course_is_approved,
        rejection_reason: l.rejection_reason,
        previous_submission: l.previous_submission,
        is_rejected: l.is_rejected,
        type: 'professor-course',
      };
    }),
  ];
  const seen = new Set();
  return pendingList.filter((item) => {
    if (!item || item.is_rejected) return false;
    const key = `${item.type}-${item.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};


const CoursesAdminDashboardPage = () => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const isAdmin = userHasInstitutionRole(
    user,
    'CoursesAdminTeam',
    selectedInstitution
  );
  const theme = useTheme();

  const controlPanelStyles = {
    flex: '1 1 250px',
    minWidth: '250px',
    maxWidth: '300px',
    flex: { xs: '1 1 100%', sm: '0 0 280px' },
    width: { xs: '100%', sm: 280 },
    display: 'flex',
    flexDirection: 'column',
    border: 1,
    boxShadow: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
  };

  const [data, setData] = useState({
    unapproved_courses: [],
    unapproved_professors: [],
    unapproved_departments: [],
    unapproved_professor_courses: [],
    pending_approval: [],
    approved_courses_by_user: [],
    approved_professors_by_user: [],
    rejected_courses_by_user: [],
    rejected_professors_by_user: [],
    rejected_departments_by_user: [],
    reported_reviews: [],
    admin_decision_reasons: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    reason: "",
    targetType: "",
    targetId: null,
    targetLinkId: null,
  });
  const [moderateDialog, setModerateDialog] = useState({
    open: false,
    action: "",
    reviewId: null,
  });
  const [reviewDecisions, setReviewDecisions] = useState({});
  const [heldReviews, setHeldReviews] = useState({});
  const [adminControl, setAdminControl] = useState({
    departments: [],
    courses: [],
    professors: [],
  });
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [highlightDepartmentId, setHighlightDepartmentId] = useState(null);
  const [highlightCourseId, setHighlightCourseId] = useState(null);
  const [highlightProfessorId, setHighlightProfessorId] = useState(null);
  const [pendingAlertOpen, setPendingAlertOpen] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(0);
  const [editDepartment, setEditDepartment] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [editProfCourse, setEditProfCourse] = useState(null);

  useEffect(() => {
    if (!adminControl.departments.some((d) => d.id === selectedDepartment)) {
      setSelectedDepartment('all');
      setSelectedCourse('');
    }
  }, [adminControl.departments, selectedDepartment]);

  useEffect(() => {
    if (!adminControl.courses.some((c) => c.id === selectedCourse)) {
      setSelectedCourse('');
    }
  }, [adminControl.courses, selectedCourse]);

  const filteredCourses = useMemo(() => {
    return selectedDepartment === 'all'
      ? adminControl.courses
      : adminControl.courses.filter(
          (c) => c.department?.id === selectedDepartment
        );
  }, [adminControl.courses, selectedDepartment]);

  const filteredProfessors = useMemo(() => {
    if (!selectedCourse) return [];
    let profs = adminControl.professors;
    if (selectedDepartment !== 'all') {
      profs = profs.filter((p) =>
        p.courses_info.some((ci) => ci.department?.id === selectedDepartment)
      );
    }
    profs = profs.filter((p) =>
      p.courses_info.some((ci) => ci.id === selectedCourse)
    );
    return profs;
  }, [adminControl.professors, selectedDepartment, selectedCourse]);

  const [pendingItems, setPendingItems] = useState([]);

  useEffect(() => {
    setPendingAlertOpen(pendingItems.length > 0);
    setPendingIndex(0);
  }, [pendingItems.length]);

  const handleHoldToggle = async (id) => {
    try {
      await api.post(`/courses/admin-dashboard/reviews/${id}/moderate/`, {
        action: "HOLD",
      });
      setHeldReviews((prev) => ({ ...prev, [id]: !prev[id] }));
    } catch {
      setError("Failed to moderate review.");
    }
  };

  const fetchDashboardData = async () => {
    try {
      setPendingItems([]);
      const params = { params: { institution_id: selectedInstitution } };
      const deptRes = await api.get(
        "/courses/pending-departments/",
        params
      );
      const courseRes = await api.get("/courses/pending-courses/", params);
      const profRes = await api.get("/courses/pending-professors/", params);
      const pendingProfCourseRes = await api.get(
        "/courses/pending-professor-courses/",
        params
      );
      const res = await api.get("/courses/admin-dashboard/", params);

      const pendingDepartments = [...(deptRes.data || [])];
      const pendingCourses = [...(courseRes.data || [])];
      const pendingProfessors = [...(profRes.data || [])];

      (res.data.unapproved_professor_courses || []).forEach((l) => {
        if (!l.course_is_approved) {
          pendingCourses.push(l.course);
        }
        pendingProfessors.push({
          ...l.professor,
          courses_info: [l.course],
          rejection_reason: l.rejection_reason,
          previous_submission: l.previous_submission,
          has_unapproved_link: !l.course_is_approved,
          ...(l.course_is_approved
            ? {}
            : {
                unapproved_course_name:
                  l.course.short_name || l.course.long_name,
                unapproved_course_id: l.course.id,
              }),
          unapproved_link_id: l.id,
          is_rejected: l.is_rejected,
        });
      });

      const pendingProfCourseLinks = [
        ...(pendingProfCourseRes.data || []),
        ...(res.data.pending_professor_courses || []),
        ...(res.data.unapproved_professor_courses || []),
      ];
      pendingProfCourseLinks.forEach((l) => {
        if (!l.course_is_approved) {
          pendingCourses.push({ ...l.course, is_rejected: l.course.is_rejected });
        }
        const prof = l.professor_submission || l.professor;
        pendingProfessors.push({
          id: prof.id,
          name: prof.name,
          courses_info: [l.course],
          rejection_reason: l.rejection_reason,
          previous_submission: l.previous_submission,
          is_submission: !!l.professor_submission,
          is_rejected: l.is_rejected,
        });
      });

      (res.data.rejected_professor_courses_by_user || []).forEach((l) => {
        if (!l.course_is_approved) {
          pendingCourses.push({ ...l.course, is_rejected: l.course.is_rejected });
        }
        const prof = l.professor || l.professor_submission;
      pendingProfessors.push({
        id: prof.id,
        name: prof.name,
        courses_info: [l.course],
        rejection_reason: l.rejection_reason,
        previous_submission: l.previous_submission,
        is_submission: !l.professor,
        is_rejected: true,
      });
      });

      const uniquePending = buildUniquePendingList(
        pendingDepartments,
        pendingCourses,
        pendingProfCourseLinks
      );

      const base = mergePendingLists(
        pendingDepartments,
        pendingCourses,
        pendingProfessors
      );

      const deptMap = new Map(base.departments.map((d) => [d.id, d]));
      const courseMap = new Map(base.courses.map((c) => [c.id, c]));
      const profMap = new Map(base.professors.map((p) => [p.id, p]));

      // Collect professor IDs for which professor-course links are pending
      const profCourseProfIds = new Set(
        (res.data.unapproved_professor_courses || []).map((l) => l.professor.id)
      );
      const merged = [
        ...(courseRes.data || []).map((c) => ({
          ...c,
          entry: `${c.short_name} – ${c.long_name}`,
          type: "course",
          is_pending: !c.is_rejected,
        })),
        // Filter out professors that already appear in the professor-course list
        ...((profRes.data || []).filter((p) => !profCourseProfIds.has(p.id))).map(
          (p) => {
            const first = p.courses_info?.[0];
            const courseName = first
              ? `${first.short_name} – ${first.long_name}`
              : "—";
            return {
              ...p,
              entry: `${p.name} – ${courseName}`,
              type: "professor",
              department: first?.department,
              is_pending: !p.is_rejected,
            };
          }
        ),
        ...(res.data.unapproved_professor_courses || []).map((l) => ({
          id: `${l.professor.id}-${l.course.id}`,
          professor_id: l.professor.id,
          course_id: l.course.id,
          entry: `${l.professor.name} – ${l.course.short_name} – ${l.course.long_name}`,
          type: "professor-course",
          department: l.course.department,
          courses_info: [
            {
              short_name: l.course.short_name,
              long_name: l.course.long_name,
            },
          ],
          course_is_approved: l.course_is_approved,
          is_rejected: l.is_rejected,
          is_pending: !l.is_rejected,
        })),
        ...(deptRes.data || []).map((d) => ({
          ...d,
          entry: d.name,
          type: "department",
          is_pending: !d.is_rejected,
        })),
      ];

      setAdminControl({
        departments: [
          { id: 'all', name: 'All', is_pending: false },
          ...Array.from(deptMap.values()).filter(
            (d) => d.is_pending || d.is_rejected || d.is_upstream
          ),
        ],
        courses: Array.from(courseMap.values())
          .filter((c) => c.is_pending || c.is_rejected || c.is_upstream)
          .map((c) => ({
            ...c,
            entry: `${c.short_name} – ${c.long_name}`,
            department: c.department
              ? {
                  ...c.department,
                  is_pending: deptMap.get(c.department.id)?.is_pending || false,
                }
              : c.department,
          })),
        professors: Array.from(profMap.values()).filter(
          (p) => p.is_pending || p.is_rejected || p.is_upstream
        ),
      });

      if (!deptMap.has(selectedDepartment) && selectedDepartment !== 'all') {
        setSelectedDepartment('all');
        setSelectedCourse('');
      }
      if (!courseMap.has(selectedCourse)) {
        setSelectedCourse('');
      }

      const rest = res.data || {};
      setData({
        ...rest,
        unapproved_departments: deptRes.data || [],
        unapproved_courses: courseRes.data || [],
        unapproved_professors: profRes.data || [],
        pending_approval: merged,
      });
      const reviews = res.data.reported_reviews || [];
      const decisions = {};
      const held = {};
      reviews.forEach((r) => {
        const vote = r.my_moderation_vote;
        if (vote) {
          if (vote.action === "REMOVE" || vote.action === "DISMISS") {
            decisions[r.id] = vote.action;
          } else if (vote.is_hold) {
            decisions[r.id] = "HOLD";
            held[r.id] = true;
          }
        }
      });
      setReviewDecisions(decisions);
      setHeldReviews(held);
      setPendingItems(uniquePending);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setForbidden(true);
      } else {
        setError("Failed to fetch dashboard data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type, id, row) => {
    setLoading(true);
    try {
      await api.post(getApproveEndpoint(type, id, row));
      await fetchDashboardData();
      setSuccessMessage('Approved.');
    } catch {
      setError(`Failed to approve ${type}.`);
    } finally {
      setLoading(false);
    }
  };


  const handleUnapprove = async (type, id) => {
    setLoading(true);
    try {
      if (type === "professor-course") {
        const [profId, courseId] = id.split("-");
        await api.post(
          `/courses/admin-dashboard/unapprove-professor-course/${profId}/${courseId}/`
        );
      } else {
        await api.post(`/courses/admin-dashboard/unapprove-${type}/${id}/`);
      }
      await fetchDashboardData();
      setSuccessMessage('Unapproved.');
    } catch {
      setError(`Failed to unapprove ${type}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = (type, id, row) => {
    setRejectDialog({
      open: true,
      reason: "",
      targetType: type,
      targetId: id,
      targetLinkId: row?.unapproved_link_id,
    });
  };

  const confirmReject = async () => {
    setLoading(true);
    try {
      await api.post(
        getRejectEndpoint(
          rejectDialog.targetType,
          rejectDialog.targetId,
          { unapproved_link_id: rejectDialog.targetLinkId }
        ),
        { reason: rejectDialog.reason }
      );
      await fetchDashboardData();
      setRejectDialog({
        open: false,
        reason: "",
        targetType: "",
        targetId: null,
        targetLinkId: null,
      });
      setSuccessMessage('Rejected.');
    } catch {
      setError(`Failed to reject ${rejectDialog.targetType}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleModerateReview = (id, action) => {
    setModerateDialog({ open: true, action, reviewId: id });
  };

  const confirmModerateReview = async (reason) => {
    try {
      await api.post(
        `/courses/admin-dashboard/reviews/${moderateDialog.reviewId}/moderate/`,
        { action: moderateDialog.action, reason }
      );
      setReviewDecisions((prev) => ({
        ...prev,
        [moderateDialog.reviewId]: moderateDialog.action,
      }));
      fetchDashboardData();
      setModerateDialog({ open: false, action: "", reviewId: null });
      setSuccessMessage('Review updated.');
    } catch {
      setError("Failed to moderate review.");
    }
  };

  const handleMergeSubmissions = async (ids) => {
    if (!ids || ids.length < 2) return;
    setLoading(true);
    try {
      const [master, ...duplicates] = ids;
      await api.post(
        "/courses/admin-dashboard/merge-professor-submissions/",
        { master, duplicates }
      );
      await fetchDashboardData();
      setSuccessMessage('Submissions merged.');
    } catch {
      setError("Failed to merge submissions.");
    } finally {
      setLoading(false);
    }
  };

  const flashHighlight = (setter, id) => {
    setter(id);
    setTimeout(() => setter(null), 1500);
  };

  const openEditDepartment = (dept) => {
    setEditDepartment(dept);
  };

  const openEditCourse = (course) => {
    setEditCourse(course);
  };

  const openEditProfCourse = (prof) => {
    const course = adminControl.courses.find((c) => c.id === selectedCourse);
    let professor = prof.professor;
    let professorSubmission = prof.professor_submission;

    if (!professor && !professorSubmission) {
      if (prof.is_submission) {
        professorSubmission = { id: prof.id, name: prof.name };
      } else {
        professor = { id: prof.id, name: prof.name };
      }
    } else {
      if (professor && !professor.name) professor = { ...professor, name: prof.name };
      if (professorSubmission && !professorSubmission.name)
        professorSubmission = { ...professorSubmission, name: prof.name };
    }

    setEditProfCourse({
      id: prof.link_id || prof.id,
      course,
      professor,
      professor_submission: professorSubmission,
      rejection_reason: prof.rejection_reason,
      previous_submission: prof.previous_submission,
    });
  };

  const handleResubmitDepartment = async (id, data) => {
    setLoading(true);
    try {
      await updateDepartment(id, data);
      await fetchDashboardData();
      setSuccessMessage('Department updated.');
    } catch {
      setError('Failed to edit entry');
    } finally {
      setEditDepartment(null);
      setLoading(false);
    }
  };

  const handleResubmitCourse = async (id, data) => {
    setLoading(true);
    try {
      await updateCourse(id, data);
      await fetchDashboardData();
      setSuccessMessage('Course updated.');
    } catch {
      setError('Failed to edit entry');
    } finally {
      setEditCourse(null);
      setLoading(false);
    }
  };

  const handleResubmitProfCourse = async (id, data) => {
    setLoading(true);
    try {
      await updateProfessorCourse(id, data);
      await fetchDashboardData();
      setSuccessMessage('Professor-course updated.');
    } catch {
      setError('Failed to edit entry');
    } finally {
      setEditProfCourse(null);
      setLoading(false);
    }
  };

  const gotoPendingDepartment = (dept) => {
    if (!dept) return;
    setSelectedDepartment(dept.id);
    setSelectedCourse('');
    flashHighlight(setHighlightDepartmentId, dept.id);
  };

  const gotoPendingCourse = (course) => {
    if (!course) return;
    const depId = course.department?.id;
    if (depId) setSelectedDepartment(depId);
    setSelectedCourse(course.id);
    flashHighlight(setHighlightCourseId, course.id);
  };

  const gotoPendingProfessor = (prof) => {
    if (!prof) return;
    const first = prof.courses_info?.[0];
    if (first) {
      if (first.department?.id) setSelectedDepartment(first.department.id);
      setSelectedCourse(first.id);
    }
    const id = prof.professor_id || prof.id;
    flashHighlight(setHighlightProfessorId, id);
  };

  const gotoPendingItem = (entry) => {
    if (!entry) return;
    if (entry.type === 'department') {
      gotoPendingDepartment(entry);
    } else if (entry.type === 'course') {
      gotoPendingCourse(entry);
    } else {
      gotoPendingProfessor(entry);
    }
  };

  const gotoNextPending = () => {
    if (pendingItems.length === 0) return;
    setPendingIndex((prev) => {
      const next = (prev + 1) % pendingItems.length;
      gotoPendingItem(pendingItems[next]);
      return next;
    });
  };

  const gotoPrevPending = () => {
    if (pendingItems.length === 0) return;
    setPendingIndex((prev) => {
      const next = (prev - 1 + pendingItems.length) % pendingItems.length;
      gotoPendingItem(pendingItems[next]);
      return next;
    });
  };

  useEffect(() => {
    if (user && isAdmin) {
      fetchDashboardData();
    } else if (user) {
      setLoading(false);
    }
  }, [user, isAdmin, selectedInstitution]);

  if (!user) {
    return <NoPermissionPage />;
  }

  if (!isAdmin || forbidden) {
    return <NoPermissionPage showLoginLink={false} />;
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Admin Dashboard
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
        Institution: {user.userprofile?.institution?.display_name || "—"}
      </Typography>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Admin Control
      </Typography>
      {pendingAlertOpen && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {pendingItems.length} pending submission
            {pendingItems.length > 1 ? 's' : ''}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <IconOnlyChip
                icon={<ArrowBackIcon fontSize="small" />}
                color="info"
                onClick={gotoPrevPending}
                sx={{ p: 1, mr: 1 }}
              />
              <IconOnlyChip
                icon={<ArrowForwardIcon fontSize="small" />}
                color="info"
                onClick={gotoNextPending}
                sx={{ p: 1 }}
              />
            </Box>
          </Box>
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignContent: 'center',
          rowGap: 2,
          columnGap: 2,
          mx: 'auto',
          mb: 4,
        }}
      >
        <AdminControlTabs
          departments={adminControl.departments}
          courses={filteredCourses}
          professors={filteredProfessors}
          selectedDept={selectedDepartment}
          selectedCourse={selectedCourse}
          highlightDept={highlightDepartmentId}
          highlightCourse={highlightCourseId}
          highlightProfessor={highlightProfessorId}
          onDepartmentSelect={(d) => {
            setSelectedDepartment(d.id);
            setSelectedCourse('');
          }}
          onCourseSelect={(c) => setSelectedCourse(c.id)}
          onApprove={handleApprove}
          onReject={handleReject}
          onEditDepartment={openEditDepartment}
          onEditCourse={openEditCourse}
          onEditProfessorCourse={openEditProfCourse}
        />
      </Box>

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Review Moderation
      </Typography>
      <ReportedReviewsTable
        data={data.reported_reviews}
        handleModerate={handleModerateReview}
        heldReviews={heldReviews}
        handleHoldToggle={handleHoldToggle}
        reviewDecisions={reviewDecisions}
      />
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ ...rejectDialog, open: false })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject {rejectDialog.targetType}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Specify Reasons for Rejection.</Typography>
          <TextField
            autoFocus
            margin="dense"
            type="text"
            fullWidth
            multiline
            minRows={4}
            value={rejectDialog.reason}
            onChange={(e) =>
              setRejectDialog({ ...rejectDialog, reason: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ ...rejectDialog, open: false })}>Cancel</Button>
          <Button variant="contained" onClick={confirmReject}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <ActionReasonDialog
        open={moderateDialog.open}
        onClose={() => setModerateDialog({ ...moderateDialog, open: false })}
        title="Moderate Review"
        reasons={data.admin_decision_reasons}
        onConfirm={(reason) => confirmModerateReview(reason)}
      />
      <EditDepartmentAdminDialog
        open={Boolean(editDepartment)}
        department={editDepartment}
        onClose={() => setEditDepartment(null)}
        onResubmit={handleResubmitDepartment}
      />
      <EditCourseAdminDialog
        open={Boolean(editCourse)}
        course={editCourse}
        onClose={() => setEditCourse(null)}
        onResubmit={handleResubmitCourse}
      />
      <EditProfessorCourseAdminDialog
        open={Boolean(editProfCourse)}
        link={editProfCourse}
        onClose={() => setEditProfCourse(null)}
        onResubmit={handleResubmitProfCourse}
      />
    </Box>
  );
};

export default CoursesAdminDashboardPage;
